import { ref, watch } from 'vue'
import type { Ref } from 'vue'
import { searchMatrixMessagesInRoom } from '@/modules/chat-matrix-fast/services/matrixChatClient'
import { searchDebugLog, summarizeSearchBodyForLog, debugConsoleError } from '@/modules/chat-matrix-fast/utils/chatDebugLog'

interface SearchMessage {
  message: {
    id: number
    content: string
    message: string
    time: string
  }
  index: number
  matrixEventId: string
  contact: {
    fullName: string
    avatar?: string | null
  }
  isMine: boolean
}

interface SearchOptions {
  debounceTime?: number
  minLength?: number
  onSearch?: (query: string) => void
}

export const useChatSearch = (
  isMessageSearchActive: Ref<boolean>,
  options: SearchOptions = {},
) => {
  const {
    debounceTime = 1500,
    minLength = 2,
    onSearch,
  } = options

  const q = ref('')
  const messageSearchResults = ref<SearchMessage[]>([])
  const isSearching = ref(false)
  let searchTimeout: ReturnType<typeof setTimeout> | null = null

  const performSearch = async (
    query: string,
    matrixRoomId: string,
    contact: { fullName?: string; avatar?: string | null },
    profileUser?: { fullName?: string; avatar?: string | null } | null,
  ) => {
    const trimmed = query.trim()
    searchDebugLog('performSearch · start', {
      query,
      trimmed,
      matrixRoomId,
      isMessageSearchActive: isMessageSearchActive.value,
      contactName: contact.fullName ?? null,
    })

    if (!trimmed || !matrixRoomId) {
      messageSearchResults.value = []
      searchDebugLog('performSearch · cleared', {
        reason: !trimmed ? 'empty-query' : 'no-room-id',
        query,
        matrixRoomId: matrixRoomId || null,
      })
      return
    }

    isSearching.value = true
    try {
      const hits = await searchMatrixMessagesInRoom(matrixRoomId, trimmed)

      messageSearchResults.value = hits.map(hit => {
        const senderName = hit.senderName.startsWith('@')
          ? (contact.fullName || hit.senderName)
          : hit.senderName

        return {
          message: {
            id: hit.id,
            content: hit.content,
            message: hit.content,
            time: hit.createdAt,
          },
          index: hit.id,
          matrixEventId: hit.matrixEventId,
          contact: hit.isMine
            ? {
                fullName: profileUser?.fullName || contact.fullName || 'Вы',
                avatar: profileUser?.avatar ?? contact.avatar ?? null,
              }
            : {
                fullName: senderName,
                avatar: hit.senderAvatar ?? contact.avatar ?? null,
              },
          isMine: hit.isMine,
        }
      })

      searchDebugLog('performSearch · mapped to UI', {
        query: trimmed,
        matrixRoomId,
        hitsCount: hits.length,
        uiResultsCount: messageSearchResults.value.length,
        uiPreview: messageSearchResults.value.slice(0, 8).map(item => ({
          matrixEventId: item.matrixEventId,
          preview: summarizeSearchBodyForLog(item.message.message),
          contact: item.contact.fullName,
          isMine: item.isMine,
        })),
      })
    } catch (error) {
      debugConsoleError('[useChatSearch] performSearch - ошибка:', error)
      messageSearchResults.value = []
      searchDebugLog('performSearch · error', {
        query: trimmed,
        matrixRoomId,
        error: error instanceof Error ? error.message : String(error),
      })
    } finally {
      isSearching.value = false
    }
  }

  watch(q, val => {
    const trimmedVal = val?.trim() || ''

    searchDebugLog('input · changed', {
      raw: val,
      trimmed: trimmedVal,
      length: trimmedVal.length,
      minLength,
      debounceTime,
      isMessageSearchActive: isMessageSearchActive.value,
    })

    if (searchTimeout) {
      clearTimeout(searchTimeout)
    }
    searchTimeout = null

    // Поле очищено — сразу наверх (сброс списка чатов или hits)
    if (trimmedVal.length === 0) {
      searchDebugLog('input · empty → onSearch immediately')
      onSearch?.('')
      return
    }

    // Короткий запрос: без debounce
    if (trimmedVal.length < minLength) {
      searchDebugLog('input · too short → onSearch immediately', {
        length: trimmedVal.length,
        minLength,
      })
      onSearch?.(val)
      return
    }

    searchDebugLog('input · debounce scheduled', {
      delayMs: debounceTime,
      query: trimmedVal,
    })
    searchTimeout = setTimeout(() => {
      searchDebugLog('input · debounce fired → onSearch', {
        query: val,
        isMessageSearchActive: isMessageSearchActive.value,
      })
      onSearch?.(val)
    }, debounceTime)
  })

  const clearSearch = () => {
    searchDebugLog('clearSearch')
    q.value = ''
    messageSearchResults.value = []
  }

  return {
    q,
    messageSearchResults,
    performSearch,
    clearSearch,
    isSearching,
  }
}
